/**
 * @fileoverview Visitor intent classification for site intelligence.
 * @description Maps route pathnames to a coarse, low-cardinality intent stage so
 * session progression and funnel analysis can be sliced in GA4/BigQuery without
 * high-cardinality path strings. Intent is derived from path structure only —
 * no PII, no storage reads.
 *
 * @module lib/site-intelligence/intent
 */

/** Coarse visitor intent stage (ordered low → high). */
export type VisitorIntent = "awareness" | "exploration" | "evaluation" | "conversion"

/** Numeric rank for comparing intent depth within a session. */
const INTENT_RANK: Readonly<Record<VisitorIntent, number>> = {
  awareness: 0,
  exploration: 1,
  evaluation: 2,
  conversion: 3,
}

/**
 * Returns the numeric rank of an intent stage (higher = closer to conversion).
 *
 * @param {VisitorIntent} intent - Intent stage label.
 * @returns {number} Rank in [0, 3].
 */
export function intentRank(intent: VisitorIntent): number {
  return INTENT_RANK[intent]
}

/**
 * Classifies a pathname into a visitor intent stage.
 *
 * Stages are deliberately coarse and stable — one label per funnel band — so
 * they group cleanly in GA4 custom dimensions and the section-strength mart.
 *
 * @param {string | undefined} path - Route pathname (may include leading slash).
 * @returns {VisitorIntent} Intent stage for the page.
 * @example
 * intentForPath('/resources/tools/video-dropoff') // 'exploration'
 * intentForPath('/pricing') // 'evaluation'
 * intentForPath('/request-access') // 'conversion'
 */
export function intentForPath(path: string | undefined): VisitorIntent {
  if (!path || path === "/") return "awareness"
  const segments = path.replace(/^\/+/, "").split("/")
  const top = segments[0] ?? ""

  switch (top) {
    case "request-access":
    case "checkout":
    case "account":
      return "conversion"
    case "pricing":
    case "frameworks":
      return "evaluation"
    case "resources":
      return "exploration"
    case "blog":
    case "about":
      return "awareness"
    default:
      return "awareness"
  }
}

/**
 * Returns the higher of two intent stages (session max intent updater).
 *
 * @param {VisitorIntent} current - Current session max intent.
 * @param {VisitorIntent} next - Intent of the page just viewed.
 * @returns {VisitorIntent} The deeper of the two stages.
 */
export function maxIntent(current: VisitorIntent, next: VisitorIntent): VisitorIntent {
  return intentRank(next) > intentRank(current) ? next : current
}
